"use client";

import { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Rive, Layout, Fit, Alignment } from "@rive-app/canvas";

/**
 * Renders a Rive animation (.riv) on a canvas element.
 *
 * Uses @rive-app/canvas directly (no React wrapper) so we have full control
 * over the Rive instance — pause on unmount, resize handling, cleanup, etc.
 */
const RivePlayer = ({ src, classes = "", fit = Fit.Cover, alignment = Alignment.Center }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!src || !canvasRef.current) return;

    const riveInstance = new Rive({
      src,
      canvas: canvasRef.current,
      autoplay: true,
      layout: new Layout({ fit, alignment }),
      // Resize the drawing surface to match the canvas CSS size so it stays
      // sharp on high-DPI screens without us doing the math manually.
      onLoad: () => riveInstance.resizeDrawingSurfaceToCanvas(),
    });

    // Keep canvas sharp when the window is resized (e.g. orientation change).
    /** Syncs the canvas drawing buffer to its current CSS dimensions. */
    const handleResize = () => riveInstance.resizeDrawingSurfaceToCanvas();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      riveInstance.cleanup();
    };
  }, [src, fit, alignment]);

  return (
    <canvas
      ref={canvasRef}
      className={classes}
      // Canvas needs explicit width/height attributes — CSS alone won't set the
      // drawing buffer size. 100%/100% is then handled by the CSS classes.
      style={{ width: "100%", height: "100%" }}
    />
  );
};

RivePlayer.propTypes = {
  src: PropTypes.string.isRequired,
  classes: PropTypes.string,
  fit: PropTypes.string,
  alignment: PropTypes.string,
};

export { Fit, Alignment };
export default RivePlayer;
